import {
  buildCustomPropertyConditionAttribute,
  getPolicyCustomPropertyCast,
  isPolicyCustomPropertyType,
  isPolicyDatePropertyType,
  type PolicyCustomPropertyEntity,
  type PolicyCustomPropertyType,
} from '@provision/sidekick-utils';
import {
  ConditionType,
  FormControlType,
} from 'src/graphql/entities/behavior/behavior-config/behavior-config.types';
import {
  BehaviorConfigOption,
  RuleControlType,
} from 'src/graphql/entities/behavior/behavior.types';
import { NormalizedCustomAttributeDefinition } from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import type { SupportPolicyConditionField } from './support-policy.conditions';
import {
  SUPPORT_POLICY_CONDITION_OPERATORS,
  SUPPORT_POLICY_DATE_CONDITION_OPERATORS,
} from './support-policy.operators';

/** Section headers in the attribute picker; built-in fields sit under "Conversation" / "Advanced". */
export const CUSTOM_PROPERTY_CONDITION_GROUP_LABEL: Record<
  PolicyCustomPropertyEntity,
  string
> = {
  Conversation: 'Conversation properties',
  Address: 'Site properties',
};

const UI_TYPE_BY_CUSTOM_PROPERTY_TYPE: Record<PolicyCustomPropertyType, FormControlType> = {
  short_text: FormControlType.Text,
  multi_line_text: FormControlType.Text,
  url: FormControlType.Text,
  number: FormControlType.Number,
  checkbox: FormControlType.List,
  dropdown: FormControlType.List,
  multi_select: FormControlType.List,
  // the shared rules value editor renders these as the relative-or-fixed date pickers
  date: FormControlType.Date,
  date_time: FormControlType.DateTime,
};

const CHECKBOX_OPTIONS: BehaviorConfigOption[] = [
  { id: 'true', value: 'true', label: 'Yes' },
  { id: 'false', value: 'false', label: 'No' },
];

/**
 * Visible definitions → condition-builder fields. The field `value` is the wire attribute the shared
 * evaluator parses (`Address/customAttributes.tier::text`), so saving needs no extra mapping.
 */
export function buildCustomPropertyConditionFields(
  entity: PolicyCustomPropertyEntity,
  definitions: NormalizedCustomAttributeDefinition[],
): SupportPolicyConditionField[] {
  return definitions.flatMap((definition) => {
    if (!isPolicyCustomPropertyType(definition.type)) {
      return [];
    }

    const attribute = buildCustomPropertyConditionAttribute(
      entity,
      definition.key,
      getPolicyCustomPropertyCast(definition.type),
    );
    const options = buildConditionOptions(definition);

    return [
      {
        name: attribute,
        value: attribute,
        attribute,
        label: definition.label,
        group: CUSTOM_PROPERTY_CONDITION_GROUP_LABEL[entity],
        uiType: UI_TYPE_BY_CUSTOM_PROPERTY_TYPE[definition.type],
        controlType: RuleControlType.ConditionControl,
        conditionType: ConditionType.Data,
        operators: isPolicyDatePropertyType(definition.type)
          ? SUPPORT_POLICY_DATE_CONDITION_OPERATORS
          : SUPPORT_POLICY_CONDITION_OPERATORS,
        options,
        fieldOptions: options,
        triggers: [],
      },
    ];
  });
}

function buildConditionOptions(
  definition: NormalizedCustomAttributeDefinition,
): BehaviorConfigOption[] {
  if (definition.type === 'checkbox') {
    return CHECKBOX_OPTIONS;
  }

  if (definition.type === 'dropdown' || definition.type === 'multi_select') {
    return definition.options.map((option) => ({
      id: option.key,
      value: option.key,
      label: option.value,
    }));
  }

  return [];
}
